import {
  CheckCircleOutlined,
  ErrorOutlined,
  QrCodeScannerOutlined,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import { Html5QrcodeScanner } from "html5-qrcode";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import Header from "components/Header";
import { useCheckInUserMutation } from "state/userApiSlice";

const QRScanner = () => {
  const theme = useTheme();
  const isNonMobile = useMediaQuery("(min-width: 700px)");
  const user = useSelector((state) => state.global.user);

  const [checkInUser] = useCheckInUserMutation();
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);

  // Keep scanner instance and an init guard (for React 18 StrictMode)
  const scannerRef = useRef(null);
  const scannerInitRef = useRef(false);

  const startScanning = () => {
    setScanResult(null);
    setScanning(true);
  };

  const onScanSuccess = async (decodedText) => {
    // Stop scanner
    if (scannerRef.current) {
      try {
        await scannerRef.current.clear();
      } catch (_) {}
      scannerRef.current = null;
      scannerInitRef.current = false;
    }
    setScanning(false);

    try {
      const result = await checkInUser({
        qrCodeData: decodedText,
        checkedInById: user._id,
        checkedInByName: user.name,
        checkedInByRole: user.role,
      }).unwrap();

      setScanResult({
        success: true,
        data: result.participant,
        message: result.message,
      });
    } catch (error) {
      setScanResult({
        success: false,
        message: error?.data?.msg || error?.msg || "Check-in failed",
      });
    }

    setResultDialogOpen(true);
  };

  const onScanFailure = () => {
    // scan failures are normal; ignore
  };

  const stopScanning = async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.clear();
      } catch (_) {}
      scannerRef.current = null;
      scannerInitRef.current = false;
    }
    setScanning(false);
  };

  const handleClose = () => {
    setResultDialogOpen(false);
    setScanResult(null);
  };

  // Initialize / cleanup scanner AFTER #qr-reader exists and scanning is true
  useEffect(() => {
    if (!scanning) return;

    const el = document.getElementById("qr-reader");
    if (!el) return;

    // Prevent duplicate init (React 18 StrictMode mounts effects twice in dev)
    if (scannerInitRef.current) return;

    const config = {
      fps: 10,
      qrbox: { width: 250, height: 250 },
      aspectRatio: 1.0,
    };

    const scanner = new Html5QrcodeScanner("qr-reader", config, false);
    scannerRef.current = scanner;
    scannerInitRef.current = true;

    scanner.render(onScanSuccess, onScanFailure);

    return () => {
      // Cleanup if scanning state flips or component unmounts
      if (scannerRef.current) {
        scannerRef.current.clear().catch(() => {});
        scannerRef.current = null;
      }
      scannerInitRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning]);

  // Final safety cleanup on unmount
  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        scannerRef.current.clear().catch(() => {});
        scannerRef.current = null;
      }
      scannerInitRef.current = false;
    };
  }, []);

  return (
    <Box m={isNonMobile ? "1.5rem 2.5rem" : "1.5rem"}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Header
          title="Entry Pass SCANNER"
          subtitle="Scan participant Entry Passes for check-in"
        />
      </motion.div>

      <Box mt={3}>
        <Card
          sx={{
            backgroundImage: "none",
            backgroundColor: theme.palette.background.alt,
            borderRadius: "10px",
            maxWidth: "600px",
            margin: "0 auto",
          }}
        >
          <CardContent sx={{ p: 3 }}>
            {!scanning ? (
              <Box sx={{ textAlign: "center" }}>
                <QrCodeScannerOutlined
                  sx={{
                    fontSize: 80,
                    color: theme.palette.secondary.main,
                    mb: 2,
                  }}
                />
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Ready to Scan
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 3, fontSize: "0.75rem" }}
                >
                  Click the button below to start scanning participant Entry
                  Passes
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  startIcon={<QrCodeScannerOutlined />}
                  onClick={startScanning}
                  sx={{
                    fontWeight: 700,
                    px: 4,
                    py: 1.2,
                    fontSize: "0.75rem",
                  }}
                >
                  Start Scanning
                </Button>
              </Box>
            ) : (
              <Box>
                <Alert severity="info" sx={{ mb: 2, fontSize: "0.75rem" }}>
                  Position the Entry Pass within the frame to scan
                </Alert>

                {/* Keep this element mounted ALWAYS so it's available for init */}
                <Box
                  id="qr-reader"
                  sx={{
                    "& video": {
                      borderRadius: "10px",
                    },
                  }}
                />

                <Button
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  onClick={stopScanning}
                  sx={{ mt: 2, fontWeight: 700, fontSize: "0.75rem" }}
                >
                  Stop Scanning
                </Button>
              </Box>
            )}

            {/* If you prefer to keep #qr-reader always in DOM (even when not scanning),
                move this element outside the conditional block and toggle with CSS:
                <Box id="qr-reader" sx={{ display: scanning ? 'block' : 'none' }} /> */}
          </CardContent>
        </Card>
      </Box>

      {/* Result Dialog */}
      <Dialog
        open={resultDialogOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#ffff",
            pb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {scanResult?.success ? (
              <CheckCircleOutlined
                sx={{ color: "success.main", fontSize: 25 }}
              />
            ) : (
              <ErrorOutlined sx={{ color: "error.main", fontSize: 25 }} />
            )}
            <Typography variant="h6" fontWeight={700}>
              {scanResult?.success ? "Check-in Successful" : "Check-in Failed"}
            </Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2, backgroundColor: "#ffff" }}>
          {scanResult?.success ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2, fontSize: "0.85rem" }}>
                {scanResult.message}
              </Alert>
              <Stack spacing={1.5}>
                <Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ fontSize: "0.7rem" }}
                  >
                    Name
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {scanResult.data?.name}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ fontSize: "0.7rem" }}
                  >
                    Registration ID
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {scanResult.data?.registrationId}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ fontSize: "0.7rem" }}
                  >
                    Email
                  </Typography>
                  <Typography variant="body2">
                    {scanResult.data?.email}
                  </Typography>
                </Box>
                {scanResult.data?.organization && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ fontSize: "0.7rem" }}
                    >
                      Organization
                    </Typography>
                    <Typography variant="body2">
                      {scanResult.data?.organization}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ fontSize: "0.7rem" }}
                  >
                    Check-in Time
                  </Typography>
                  <Typography variant="body2">
                    {new Date(scanResult.data?.checkInTime).toLocaleString()}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          ) : (
            <Alert severity="error" sx={{ fontSize: "0.65rem" }}>
              {scanResult?.message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, backgroundColor: "#ffff" }}>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleClose}
            sx={{ fontWeight: 700, fontSize: "0.75rem" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QRScanner;
